import { Autocomplete, TextField, Box, SxProps, Theme } from '@mui/material';
import { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Crypto } from 'src/Modules/Arbitrage/types';
import { searchCryptos } from 'src/Api/cryptos';
import { CryptoIcon } from 'src/Common';
import { debounce } from 'lodash';

interface Props {
  onFindCrypto: (crypto: Crypto) => void;
  sx?: SxProps<Theme>;
  allCryptos?: boolean;
  label?: string;
  placeholder?: string;
}

export const FindCrypto = ({
  onFindCrypto,
  sx,
  allCryptos = true,
  label = 'Search crypto',
  placeholder = 'Type symbol or name...'
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 300),
    []
  );

  const { data: cryptos, isLoading } = useQuery(
    ['cryptos-search', searchTerm, allCryptos],
    () => searchCryptos(searchTerm, allCryptos),
    {
      enabled: searchTerm.length >= 2,
      staleTime: 30000
    }
  );

  return (
    <Autocomplete
      sx={sx}
      size="small"
      options={cryptos ?? []}
      loading={isLoading}
      inputValue={inputValue}
      onInputChange={(_, value) => {
        setInputValue(value);
        debouncedSetSearchTerm(value);
      }}
      onChange={(_, value) => {
        if (value) {
          onFindCrypto(value);
        }
      }}
      getOptionLabel={option => `${option.symbol} - ${option.name}`}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <CryptoIcon
            symbol={option.symbol}
            slug={option.slug}
            style={{ height: '1.2rem', width: '1.2rem' }}
          />
          <span>
            <strong>{option.symbol}</strong> - {option.name}
          </span>
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
        />
      )}
      noOptionsText={
        searchTerm.length < 2
          ? 'Type at least 2 characters'
          : 'No cryptos found'
      }
    />
  );
};
